import { useEffect, useRef, useState } from "react";
import { PhaseEventGate, phaseKey, type PhasePulse } from "../components/game/phasePresentation";
import type { RoomSnapshot } from "./useRoomConnection";
import { useVisualEffects } from "./VisualEffectsContext";

export function usePhaseVfx(snapshot: RoomSnapshot | null, connected: boolean) {
  const { reduced } = useVisualEffects();
  const gate = useRef(new PhaseEventGate());
  const latest = useRef({ snapshot, received: Date.now() });
  const keyboard = useRef(false);
  const [pulse, setPulse] = useState<PhasePulse | null>(null);
  const [entry, setEntry] = useState<string | null>(null);

  useEffect(() => {
    latest.current = { snapshot, received: Date.now() };
    if (!snapshot) return;
    const next = gate.current.observe(snapshot, { connected, visible: !document.hidden, reduced, keyboard: keyboard.current });
    setEntry(next.transition);
    setPulse(previous => next.pulse ?? (connected && !reduced && !document.hidden && previous?.phaseKey === phaseKey(snapshot) ? previous : null));
  }, [snapshot, connected, reduced]);

  useEffect(() => {
    const onKey = () => { keyboard.current = true; };
    const onPointer = () => { keyboard.current = false; };
    const onVisibility = () => {
      setPulse(null);
      setEntry(null);
      if (!document.hidden && latest.current.snapshot) {
        // Mốc theo đồng hồ server để bỏ sự kiện xảy ra khi tab bị ẩn hoặc bị đóng băng.
        gate.current.resumeAfter(latest.current.snapshot.serverNowMs + Date.now() - latest.current.received);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointer);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  useEffect(() => {
    if (!pulse) return;
    const timer = window.setTimeout(() => setPulse(null), 580);
    return () => window.clearTimeout(timer);
  }, [pulse]);

  const current = snapshot ? phaseKey(snapshot) : null;
  return { pulse: !reduced && connected && pulse?.phaseKey === current ? pulse : null, enter: !reduced && connected && entry === current };
}
